/**
 * The file archive (docs/specs/cloud/file-archive.md; MB decisions 5, 102, 376; sign-off 06-054): every file Teslyn
 * Field imports or exports is kept, by project, year and day, in the tenant's archive. A tenant's archive is Teslyn
 * Cloud storage by default, or the tenant's own Nextcloud over WebDAV (decision 376). Teslyn Field always sends to
 * Teslyn Cloud (Edge Function `archive-upload`), which forwards to the Nextcloud with the app password held in Vault:
 * the app password never reaches a laptop.
 *
 * This file has no imports on purpose: the Edge Functions (Deno) use an identical copy,
 * `supabase/functions/_shared/archive.ts`, and `test/archive.test.ts` fails if the two differ. Edit this file, then
 * run `node tools/dev/copy-edge-shared.mjs`.
 *
 * Nextcloud's answers (status codes and bodies) are classified from genuine answers of Nextcloud 31.0.14 captured on
 * 20 Oct 2026 (tests/fixtures/nextcloud/README.md) and its documentation (WebDAV basics, chunked file upload v2).
 */

export type ArchiveDestination = 'survera' | 'nextcloud';
export type ArchiveSource = 'import' | 'export';

const MIB = 1024 * 1024;

/** One request to Teslyn Cloud carries at most this much of a file; Nextcloud chunks are this size (last smaller). */
export const ARCHIVE_PIECE_BYTES = 8 * MIB;
/** Nextcloud chunked upload v2: every chunk but the last is at least 5 MiB (needed with S3 primary storage). */
export const NEXTCLOUD_MIN_CHUNK_BYTES = 5 * MIB;
/** Nextcloud chunked upload v2 numbers chunks 1 to 10000. */
export const NEXTCLOUD_MAX_CHUNKS = 10_000;
/** The archive bucket's limit (decision 391: 200 MB; the Supabase project's own upload limit must allow it). */
export const ARCHIVE_TESLYN_MAX_BYTES = 200 * MIB;
/** The largest file sent to a Nextcloud (any file the Diurnal tool archived is far smaller). */
export const ARCHIVE_NEXTCLOUD_MAX_BYTES = 5 * 1024 * MIB;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const SHA256 = /^[0-9a-f]{64}$/;
const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

// ---------------------------------------------------------------------------------------------- layout

/**
 * `{ year: '2026', day: 'Day289' }` for `2026-10-16`: the Diurnal tool's folders (`<project>/<year>/Day<ddd>`,
 * cloudflare-api upload.js), the day of the year in three digits. Teslyn uses the laptop's date when the file was
 * imported or exported, not the day it reached the server, so files queued offline land on their own day.
 */
export function archiveDayFolder(day: string): { readonly year: string; readonly day: string } {
  const m = DAY.exec(day);
  if (!m) throw new Error(`${day} is not a day (yyyy-mm-dd).`);
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const t = Date.UTC(y, mo - 1, d);
  const back = new Date(t);
  if (back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) throw new Error(`${day} is not a day.`);
  const n = Math.round((t - Date.UTC(y, 0, 0)) / 86_400_000);
  return { year: String(y), day: `Day${String(n).padStart(3, '0')}` };
}

/** The project's folder: its number with anything but letters, digits, `_` and `-` made `_`, at most 50 (as before). */
export function archiveProjectFolder(projectNumber: string): string {
  const clean = projectNumber
    .trim()
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .slice(0, 50);
  return clean || 'No_project';
}

/** The file's own name (no folders), with anything but letters, digits, `.`, `_` and `-` made `_` (as before). */
export function archiveFileName(path: string): string {
  const base = path.replace(/^.*[\\/]/, '');
  const clean = base.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 255);
  return /^\.*$/.test(clean) ? 'file' : clean;
}

/** A second, different file of the same name on the same day: `MAG0289-<first 8 of its SHA-256>.dbt`. */
export function archiveAltName(fileName: string, sha256: string): string {
  const dot = fileName.lastIndexOf('.');
  const tag = sha256.slice(0, 8);
  return dot > 0 ? `${fileName.slice(0, dot)}-${tag}${fileName.slice(dot)}` : `${fileName}-${tag}`;
}

export interface ArchiveName {
  readonly projectNumber: string;
  /** The laptop's date of the import or export, yyyy-mm-dd. */
  readonly day: string;
  readonly fileName: string;
}

/** `F26048/2026/Day289/MAG0289.dbt`: where a file goes inside the tenant's Nextcloud folder. */
export function archiveRelativePath(n: ArchiveName): string {
  const { year, day } = archiveDayFolder(n.day);
  return `${archiveProjectFolder(n.projectNumber)}/${year}/${day}/${archiveFileName(n.fileName)}`;
}

/** `tenants/<tenant>/projects/<project>/archive/<year>/Day<ddd>/<name>` in the `archive` bucket (ADR-016 layout). */
export function archiveStoragePath(tenantId: string, projectId: string, n: ArchiveName): string {
  if (!UUID.test(tenantId) || !UUID.test(projectId))
    throw new Error('The tenant or project id is not valid.');
  const { year, day } = archiveDayFolder(n.day);
  return `tenants/${tenantId}/projects/${projectId}/archive/${year}/${day}/${archiveFileName(n.fileName)}`;
}

const encodeSegments = (path: string): string[] =>
  path
    .split('/')
    .filter((s) => s.length > 0)
    .map(encodeURIComponent);

/** `/remote.php/dav/files/<user>/<folder>/<relative>`, each segment URL-encoded. */
export function nextcloudDavPath(userId: string, folder: string, relative: string): string {
  return [
    '/remote.php/dav/files',
    encodeURIComponent(userId),
    ...encodeSegments(folder),
    ...encodeSegments(relative),
  ]
    .join('/')
    .replace(/^\/+/, '/');
}

/** `/remote.php/dav/uploads/<user>/<transfer>`: the chunked upload's folder. */
export function nextcloudUploadPath(userId: string, transfer: string): string {
  return `/remote.php/dav/uploads/${encodeURIComponent(userId)}/${encodeURIComponent(transfer)}`;
}

/**
 * The chunked transfer's name, from the file's content and size: a retried upload of the same file reuses the
 * chunks already sent, and two laptops sending the same file write the same chunks.
 */
export function archiveTransferId(sha256: string, size: number): string {
  return `teslyn-${sha256.slice(0, 32)}-${size}`;
}

export interface ArchivePiece {
  /** Nextcloud's chunk number, from 1. */
  readonly n: number;
  readonly offset: number;
  readonly length: number;
}

/** The pieces of a file of `size` bytes, each one request to Teslyn Cloud and one Nextcloud chunk. */
export function nextcloudPieces(size: number): ArchivePiece[] {
  const out: ArchivePiece[] = [];
  for (let offset = 0, n = 1; offset < size; offset += ARCHIVE_PIECE_BYTES, n++) {
    out.push({ n, offset, length: Math.min(ARCHIVE_PIECE_BYTES, size - offset) });
  }
  return out;
}

export type ArchiveFileCheck =
  | { readonly ok: true }
  | { readonly ok: false; readonly code: 'empty' | 'too_large'; readonly message: string };

/** Sizes as Nextcloud and Teslyn show them: MB of 1024 × 1024 bytes. */
const mb = (bytes: number): string => `${(bytes / MIB).toFixed(1)} MB`;
const mbWhole = (bytes: number): string => `${Math.round(bytes / MIB)} MB`;

/** Whether a file of `size` bytes may go to `destination`. */
export function checkArchiveFile(
  destination: ArchiveDestination,
  _name: string,
  size: number,
): ArchiveFileCheck {
  if (size <= 0)
    return { ok: false, code: 'empty', message: 'The file is empty, so there is nothing to archive.' };
  const max = destination === 'survera' ? ARCHIVE_TESLYN_MAX_BYTES : ARCHIVE_NEXTCLOUD_MAX_BYTES;
  if (size > max) {
    return {
      ok: false,
      code: 'too_large',
      message:
        destination === 'survera'
          ? `The file is ${mb(size)}; the Teslyn Cloud archive takes files up to ${mbWhole(max)}. A tenant admin can archive larger files to your organisation’s Nextcloud (Teslyn Cloud, Integrations).`
          : `The file is ${mb(size)}; Teslyn archives files up to ${mbWhole(max)}.`,
    };
  }
  return { ok: true };
}

export function isSha256(v: string): boolean {
  return SHA256.test(v);
}

export function isArchiveDay(v: string): boolean {
  try {
    archiveDayFolder(v);
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------------------------- settings

export interface NextcloudSettings {
  /** The server's address: https, no trailing slash, any sub-folder Nextcloud is installed in kept. */
  readonly url: string;
  /** The archive folder in the user's files: `/` then folders separated by `/`, no trailing slash (`/` alone: root). */
  readonly folder: string;
}

export type SettingsCheck =
  { readonly ok: true; readonly value: NextcloudSettings } | { readonly ok: false; readonly error: string };

// ------------------------------------------------------------------------------- addresses Teslyn refuses

/**
 * Where an address points that Teslyn Cloud must never send a request to (security review 27 Oct 2026, M1: a tenant
 * admin's Nextcloud address is used by the Edge Functions, so it could otherwise aim them at Teslyn Cloud's own
 * network). `local`: this computer or a private network (loopback, RFC 1918, CGNAT, ULA, `localhost`, `*.local`,
 * `*.internal`); a developer may allow these for a Nextcloud on their own machine. `reserved`: never allowed
 * (unspecified, link-local and the cloud metadata addresses, multicast, broadcast, reserved).
 */
export type AddressRefusal = 'local' | 'reserved';

export interface AddressOptions {
  /** Allow this computer and private networks, plain http and any port: local development only. */
  readonly allowLocal?: boolean;
}

const LOCAL_NAME = /^(localhost|ip6-localhost|ip6-loopback)$|\.(localhost|local|internal)$/i;
const IPV4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

/** The four bytes of a dotted quad (as the URL parser gives it: decimal, octal and hex forms already normalised). */
function parseIpv4(s: string): readonly number[] | null {
  const m = IPV4.exec(s);
  if (!m) return null;
  const b = [Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4])];
  return b.every((n) => n <= 255) ? b : null;
}

/** The eight 16-bit groups of an IPv6 address (with `::` and an embedded dotted quad, `::ffff:127.0.0.1`). */
function parseIpv6(s: string): readonly number[] | null {
  let text = s.replace(/^\[|\]$/g, '');
  const zone = text.indexOf('%');
  if (zone >= 0) text = text.slice(0, zone);
  if (!/^[0-9a-f:.]+$/i.test(text) || text.indexOf(':::') >= 0) return null;
  const halves = text.split('::');
  if (halves.length > 2) return null;
  const groups = (part: string): number[] | null => {
    if (part === '') return [];
    const out: number[] = [];
    const items = part.split(':');
    for (let i = 0; i < items.length; i++) {
      const item = items[i] ?? '';
      if (item.includes('.')) {
        if (i !== items.length - 1) return null;
        const v4 = parseIpv4(item);
        if (!v4) return null;
        out.push(((v4[0] ?? 0) << 8) | (v4[1] ?? 0), ((v4[2] ?? 0) << 8) | (v4[3] ?? 0));
      } else {
        if (!/^[0-9a-f]{1,4}$/i.test(item)) return null;
        out.push(parseInt(item, 16));
      }
    }
    return out;
  };
  const head = groups(halves[0] ?? '');
  const tail = halves.length === 2 ? groups(halves[1] ?? '') : [];
  if (!head || !tail) return null;
  if (halves.length === 2) {
    if (head.length + tail.length > 7) return null;
    return [...head, ...new Array<number>(8 - head.length - tail.length).fill(0), ...tail];
  }
  return head.length === 8 ? head : null;
}

function ipv4Refusal(b: readonly number[]): AddressRefusal | null {
  const [a, b1] = [b[0] ?? 0, b[1] ?? 0];
  if (a === 0) return 'reserved'; // 0.0.0.0/8, "this network"
  if (a === 169 && b1 === 254) return 'reserved'; // link-local, 169.254.169.254 (cloud metadata)
  if (a >= 224) return 'reserved'; // multicast 224/4, reserved 240/4, broadcast
  if (a === 127) return 'local'; // loopback
  if (a === 10) return 'local'; // RFC 1918
  if (a === 172 && b1 >= 16 && b1 <= 31) return 'local';
  if (a === 192 && b1 === 168) return 'local';
  if (a === 100 && b1 >= 64 && b1 <= 127) return 'local'; // CGNAT 100.64/10
  return null;
}

function ipv6Refusal(g: readonly number[]): AddressRefusal | null {
  const first = g[0] ?? 0;
  const zeros = (from: number, to: number): boolean => g.slice(from, to).every((x) => x === 0);
  const v4Inside = (): AddressRefusal | null => {
    const hi = g[6] ?? 0;
    const lo = g[7] ?? 0;
    return ipv4Refusal([hi >> 8, hi & 0xff, lo >> 8, lo & 0xff]);
  };
  if (zeros(0, 8)) return 'reserved'; // ::  (unspecified)
  if (zeros(0, 7) && g[7] === 1) return 'local'; // ::1 (loopback)
  if (zeros(0, 5) && g[5] === 0xffff) return v4Inside(); // ::ffff:a.b.c.d (IPv4-mapped): the IPv4 decides
  if (zeros(0, 6)) return 'reserved'; // ::a.b.c.d (IPv4-compatible, deprecated): never used for a real server
  if (first === 0x64 && g[1] === 0xff9b && zeros(2, 6)) return v4Inside(); // 64:ff9b::a.b.c.d (NAT64)
  if ((first & 0xffc0) === 0xfe80) return 'reserved'; // link-local fe80::/10
  if ((first & 0xff00) === 0xff00) return 'reserved'; // multicast ff00::/8
  if ((first & 0xfe00) === 0xfc00) return 'local'; // unique local fc00::/7 (fd00:ec2::254 among them)
  return null;
}

/**
 * Why a literal IP address is refused, or null when it is a public address. Loopback, private (RFC 1918), CGNAT and
 * unique local addresses are `local`; unspecified, link-local (the cloud metadata address 169.254.169.254 among
 * them), multicast, broadcast and reserved blocks are `reserved`. IPv4 inside IPv6 (`::ffff:127.0.0.1`,
 * `::ffff:7f00:1`, `::127.0.0.1`, `64:ff9b::7f00:1`) is judged as the IPv4 address.
 */
export function ipAddressRefusal(ip: string): AddressRefusal | null {
  const v4 = parseIpv4(ip);
  if (v4) return ipv4Refusal(v4);
  const v6 = parseIpv6(ip);
  if (v6) return ipv6Refusal(v6);
  return 'reserved'; // not an address at all: never trust it
}

/**
 * Why a URL's host name is refused before any lookup, or null when it may be looked up: `localhost`, `*.localhost`,
 * `*.local` and `*.internal` (`metadata.google.internal`) are `local`; a literal IP address is judged by
 * `ipAddressRefusal`. With `allowLocal`, `local` refusals pass (`reserved` never does).
 */
export function hostRefusal(hostname: string, options: AddressOptions = {}): AddressRefusal | null {
  const host = hostname.replace(/\.+$/, '').toLowerCase();
  let why: AddressRefusal | null;
  if (!host) why = 'reserved';
  else if (LOCAL_NAME.test(host)) why = 'local';
  else if (host.startsWith('[') || IPV4.test(host)) why = ipAddressRefusal(host);
  else why = null;
  return why === 'local' && options.allowLocal ? null : why;
}

/** The words a tenant admin sees when an address is refused (save time) or a stored one is no longer sent to. */
export const ADDRESS_REFUSED_TEXT =
  'Teslyn cannot reach that address: it is a private or local network address. Enter the public address you open Nextcloud at, for example https://nextcloud.example.com.';

/**
 * Check the server address and folder a tenant admin typed. People paste what their browser shows, so a missing
 * `https://`, a trailing slash, a Files app address (`…/index.php/apps/files/?dir=…`) or a WebDAV address are tidied
 * to the server's address. The address must be https on the standard port and must not point at this computer or
 * a private network (`hostRefusal`); a developer sets `allowLocal` for a Nextcloud on their own machine (plain http
 * and any port are then allowed too). The host's DNS answer is checked by the Edge Functions
 * (`_shared/outbound-guard.ts`), not here: this file runs in the browser as well.
 */
export function checkNextcloudSettings(input: unknown, options: AddressOptions = {}): SettingsCheck {
  const b = input && typeof input === 'object' ? (input as Record<string, unknown>) : {};
  let raw = typeof b.url === 'string' ? b.url.trim() : '';
  if (!raw)
    return {
      ok: false,
      error: 'Enter the address of your Nextcloud, for example https://nextcloud.example.com.',
    };
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(raw)) raw = `https://${raw}`;
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return { ok: false, error: 'The Nextcloud address is not a web address. Copy it from your browser.' };
  }
  if (u.username || u.password) {
    return { ok: false, error: 'Enter the Nextcloud address without a username or password in it.' };
  }
  const refused = hostRefusal(u.hostname, options);
  if (refused) return { ok: false, error: ADDRESS_REFUSED_TEXT };
  const local = options.allowLocal === true && hostRefusal(u.hostname) === 'local';
  if (u.protocol !== 'https:' && !(u.protocol === 'http:' && local)) {
    return {
      ok: false,
      error: 'The Nextcloud address must start with https:// so the app password travels encrypted.',
    };
  }
  if (u.port !== '' && !local) {
    return {
      ok: false,
      error:
        'Teslyn connects to Nextcloud on the standard https port only: enter the address without a port.',
    };
  }
  let path = u.pathname;
  const cut = path.search(/\/(index\.php|remote\.php|ocs|apps)(\/|$)/);
  if (cut >= 0) path = path.slice(0, cut);
  path = path.replace(/\/+$/, '');
  const url = `${u.protocol}//${u.host}${path}`;
  if (url.length > 300) return { ok: false, error: 'The Nextcloud address is too long.' };

  const rawFolder = typeof b.folder === 'string' ? b.folder.trim() : '';
  if (!rawFolder) return { ok: false, error: 'Enter the folder Teslyn archives into, for example /Teslyn.' };
  const parts = rawFolder
    .replace(/\\/g, '/')
    .split('/')
    .filter((s) => s.length > 0);
  if (parts.some((s) => s === '.' || s === '..')) {
    return {
      ok: false,
      error: 'The folder cannot contain . or .. : give its full path from the top of your files.',
    };
  }
  if (parts.some((s) => s.length > 255) || rawFolder.length > 1000) {
    return { ok: false, error: 'The folder name is too long.' };
  }
  return { ok: true, value: { url, folder: `/${parts.join('/')}` } };
}

/** The host shown in messages: `nextcloud.thomsonaviation.com.au`. */
export function nextcloudHost(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/** What the import and export screens say about where files go (sign-off 06-054: "the import screen names the place"). */
export function archivePlaceText(
  place: {
    readonly destination: ArchiveDestination;
    readonly url?: string | null;
    readonly folder?: string | null;
    /** Decision 393: false when a tenant admin switched archiving off. */
    readonly enabled?: boolean;
  } | null,
): string {
  if (!place)
    return 'Imported and exported files are archived when Teslyn Field is next signed in and online.';
  if (place.enabled === false) {
    return 'Archiving is switched off for your organisation: imported and exported files are not archived.';
  }
  if (place.destination === 'nextcloud' && place.url) {
    return `Imported and exported files are archived in your organisation’s Nextcloud: ${nextcloudHost(place.url)}, folder ${
      place.folder ?? '/'
    }.`;
  }
  return 'Imported and exported files are archived in Teslyn Cloud.';
}

// ---------------------------------------------------------------------------------------------- Nextcloud's answers

/**
 * What went wrong with a Nextcloud, in the terms a tenant admin can act on:
 * - `wrong_password`: 401 (the username or app password is wrong, or the app password was revoked);
 * - `folder_missing`: the archive folder (or a folder above a file) does not exist: 404 on the folder or a file's
 *   folder, 409 for MKCOL under a missing folder;
 * - `quota_full`: 507 Insufficient Storage;
 * - `unreachable`: no answer, a timeout, 429, 502, 504 or another 5xx: try again later;
 * - `maintenance`: 503 (Nextcloud's maintenance mode): try again later;
 * - `forbidden`: 403, the user may not write there;
 * - `not_nextcloud`: the address answers, but not as a Nextcloud (a web page, 405 to PROPFIND);
 * - `transfer_gone`: a chunk was sent to a chunked upload Nextcloud has already cleaned up: start again;
 * - `server_error`: anything else (400 from a chunked upload whose pieces do not add up, 423 locked);
 * - `address_refused`: no request was sent: the stored address is, or now resolves to, a private or local network
 *   address (`hostRefusal`, `_shared/outbound-guard.ts`); a tenant admin corrects it.
 */
export type NextcloudErrorCode =
  | 'wrong_password'
  | 'folder_missing'
  | 'quota_full'
  | 'unreachable'
  | 'maintenance'
  | 'forbidden'
  | 'not_nextcloud'
  | 'transfer_gone'
  | 'server_error'
  | 'address_refused';

/** Which call an answer belongs to: OCS user, PROPFIND of the archive folder, MKCOL, a file PUT/PROPFIND, a chunk. */
export type NextcloudStep = 'user' | 'folder' | 'mkcol' | 'file' | 'chunk';

const looksHtml = (body: string): boolean => /^\s*(<!doctype html|<html)/i.test(body);

/** Classify one Nextcloud answer; null when it is a success for that step. */
export function classifyNextcloudAnswer(
  status: number,
  body: string,
  step: NextcloudStep,
): NextcloudErrorCode | null {
  if (status === 401) return 'wrong_password';
  if (status === 507) return 'quota_full';
  if (status === 503) return 'maintenance';
  if (status === 403) return 'forbidden';
  if (status === 429 || status === 502 || status === 504 || status === 408) return 'unreachable';
  if (status >= 500) return 'unreachable';
  if (step === 'mkcol') {
    if (status === 201 || status === 405) return null;
    if (status === 409 || status === 404) return 'folder_missing';
    return 'server_error';
  }
  if (step === 'user') {
    if (status === 200 && /"ocs"\s*:/.test(body)) return null;
    if (status === 200 || status === 404 || status === 405 || looksHtml(body)) return 'not_nextcloud';
    return 'server_error';
  }
  if (step === 'folder') {
    if (status === 207 && /multistatus/i.test(body)) return null;
    if (status === 404 || status === 409) return 'folder_missing';
    if (status === 405 || status === 200 || looksHtml(body)) return 'not_nextcloud';
    return 'server_error';
  }
  if (step === 'chunk') {
    if (status === 201 || status === 204) return null;
    if (status === 404) return 'transfer_gone';
    if (status === 409) return 'folder_missing';
    return 'server_error';
  }
  // file: PUT 201 (new) or 204 (replaced), PROPFIND 207
  if (status === 201 || status === 204 || (status === 207 && /multistatus/i.test(body))) return null;
  if (status === 404 || status === 409) return 'folder_missing';
  if (status === 405 || status === 200 || looksHtml(body)) return 'not_nextcloud';
  return 'server_error';
}

/** `Insufficient space in …, 26214400 required, 18872368 available` → the two numbers (Sabre's 507 message). */
export function parseInsufficientStorage(
  body: string,
): { readonly required: number; readonly available: number } | null {
  const m = /(\d+)\s+required,\s+(-?\d+)\s+available/.exec(body);
  return m ? { required: Number(m[1]), available: Math.max(0, Number(m[2])) } : null;
}

export interface NextcloudErrorContext {
  readonly host: string;
  readonly folder: string;
  readonly user: string;
  readonly required?: number;
  readonly available?: number;
}

/** A Nextcloud problem in words, naming the server, folder and user (never the app password). */
export function nextcloudErrorText(code: NextcloudErrorCode, c: NextcloudErrorContext): string {
  switch (code) {
    case 'wrong_password':
      return `Nextcloud refused the username or app password for ${c.user}. Create a new app password in Nextcloud (Settings, Security) and replace it in Teslyn Cloud, Integrations.`;
    case 'folder_missing':
      return `The folder ${c.folder} does not exist in the Nextcloud files of ${c.user}. Create it in Nextcloud, or correct the folder in Teslyn Cloud, Integrations.`;
    case 'quota_full': {
      const amounts =
        c.required !== undefined && c.available !== undefined
          ? `: the file needs ${mb(c.required)} and ${mb(c.available)} is free`
          : '';
      return `The Nextcloud account ${c.user} is full${amounts}. Free up space or ask for a larger quota in Nextcloud; files wait and upload once there is room.`;
    }
    case 'unreachable':
      return `Teslyn Cloud could not reach ${c.host}. Files wait and upload later.`;
    case 'maintenance':
      return `${c.host} is in maintenance mode. Files wait and upload once it is back.`;
    case 'forbidden':
      return `Nextcloud says ${c.user} may not add files to ${c.folder}. Give the user write access to the folder in Nextcloud, or choose another folder in Teslyn Cloud, Integrations.`;
    case 'not_nextcloud':
      return `${c.host} answered, but not as a Nextcloud server. Check the address in Teslyn Cloud, Integrations: it is the address you open Nextcloud at in a browser.`;
    case 'transfer_gone':
      return `${c.host} discarded part of an upload. Teslyn starts that file again.`;
    case 'server_error':
      return `${c.host} could not store the file. Teslyn will try to upload later.`;
    case 'address_refused':
      return `Teslyn does not send files to ${c.host}: it is a private or local network address. A tenant admin enters the public Nextcloud address in Teslyn Cloud, Integrations.`;
  }
}

/** Problems a person must fix in Nextcloud or Integrations (the integration shows "Not working"); the rest pass. */
export function nextcloudNeedsAdmin(code: NextcloudErrorCode): boolean {
  return (
    code === 'wrong_password' ||
    code === 'folder_missing' ||
    code === 'quota_full' ||
    code === 'forbidden' ||
    code === 'not_nextcloud' ||
    code === 'address_refused'
  );
}

export interface DavProps {
  readonly collection: boolean;
  /** `d:getcontentlength` of a file. */
  readonly size: number | null;
  /** `d:quota-available-bytes` of a folder (negative values, "unknown" or "unlimited", are null). */
  readonly available: number | null;
  /** `oc:permissions` holds C (create file) and K (create folder): Teslyn may add its folders and files. */
  readonly canAddFiles: boolean;
}

/** The first response of a PROPFIND multistatus (Depth 0), read with plain patterns (no XML parser in Deno). */
export function parseMultistatus(body: string): DavProps | null {
  if (!/<([a-z]+:)?multistatus[\s>]/i.test(body)) return null;
  const response = /<([a-z]+:)?response[\s>][\s\S]*?<\/([a-z]+:)?response>/i.exec(body)?.[0] ?? body;
  // only the properties Nextcloud found (the 200 OK propstat)
  const found = [...response.matchAll(/<([a-z]+:)?propstat[\s>][\s\S]*?<\/([a-z]+:)?propstat>/gi)]
    .map((m) => m[0])
    .filter((p) => /<([a-z]+:)?status>[^<]*\s200\s/i.test(p))
    .join('');
  const value = (name: string): string | null => {
    const m = new RegExp(`<([a-z]+:)?${name}>([^<]*)</([a-z]+:)?${name}>`, 'i').exec(found);
    return m ? (m[2] ?? '').trim() : null;
  };
  const num = (v: string | null): number | null => {
    if (v === null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? n : null;
  };
  const permissions = value('permissions') ?? '';
  return {
    collection: /<([a-z]+:)?collection\s*\/>/i.test(found),
    size: num(value('getcontentlength')),
    available: num(value('quota-available-bytes')),
    canAddFiles: permissions.includes('C') && permissions.includes('K'),
  };
}
